import * as React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

/**
 * Bloco vazio canônico (ícone + título + descrição + ação). Uma aparência única
 * para "Nenhum X encontrado" em vez de cada tela reinventar. Distinga sempre
 * vazio real de ERRO (use a variante de erro / `isError`), nunca renderize
 * "vazio" quando a query falhou.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-dashed p-10 text-center",
        className,
      )}
    >
      {Icon && (
        <div className="mb-3 grid size-12 place-items-center rounded-full bg-muted text-muted-foreground">
          <Icon className="size-6" />
        </div>
      )}
      <p className="font-medium">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Bloco de ERRO canônico (medalhão de alerta + mensagem + "Tentar novamente").
 * Use quando `isError` da query for true, NUNCA renderize `EmptyState` nesse caso:
 * um fetch que falhou não é "nenhum resultado", e tratar como vazio esconde o
 * problema do usuário.
 */
export function ErrorState({
  title = "Não foi possível carregar",
  description = "Ocorreu um erro ao buscar os dados. Verifique sua conexão e tente novamente.",
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-dashed border-destructive/40 p-10 text-center",
        className,
      )}
    >
      <div className="mb-3 grid size-12 place-items-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="size-6" />
      </div>
      <p className="font-medium">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw className="mr-2 size-4" />
          Tentar novamente
        </Button>
      )}
    </div>
  );
}

interface LoadingStateProps {
  variant?: "grid" | "list";
  count?: number;
  className?: string;
}

/**
 * Placeholder de carregamento com skeleton (grid de cards ou lista de linhas),
 * no lugar de "Carregando..." cru.
 */
export function LoadingState({
  variant = "grid",
  count = 6,
  className,
}: LoadingStateProps) {
  if (variant === "grid") {
    return (
      <div
        className={cn(
          "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3",
          className,
        )}
      >
        {Array.from({ length: count }).map((_, index) => (
          <Skeleton key={index} className="h-40 rounded-lg" />
        ))}
      </div>
    );
  }
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-14 w-full rounded-lg" />
      ))}
    </div>
  );
}
